import React, { ReactNode, createContext, useContext, useState } from 'react'

import type { CandidateNote } from 'recruiter/graphql/candidateNotes.gql'
import type { CompanyNote } from 'recruiter/graphql/companyNotes.gql'

type Note = CandidateNote | CompanyNote | null

interface NotesContextValues {
    isEditingNote: boolean
    selectedNote: Note
    openEditNote: (note: Note) => void
    closeEditNote: () => void
}

const NotesContext = createContext<NotesContextValues | undefined>(undefined)

export const useNotesContext = () => {
    const context = useContext(NotesContext)
    if (!context) {
        throw new Error('useNotesContext must be used within a NotesProvider')
    }
    return context
}

// Provider component
interface NotesProviderProps {
    children: ReactNode
}

export const NotesProvider: React.FC<NotesProviderProps> = ({ children }) => {
    const [isEditingNote, setIsEditingNote] = useState(false)
    const [selectedNote, setSelectedNote] = useState<Note>(null)

    // Function to open the EditNotes modal with a selected note
    const openEditNote = (note: Note) => {
        setSelectedNote(note)
        setIsEditingNote(true)
    }

    // Function to close the EditNotes modal
    const closeEditNote = () => {
        setIsEditingNote(false)
        setSelectedNote(null)
    }

    return (
        <NotesContext.Provider
            value={{
                isEditingNote,
                selectedNote,
                openEditNote,
                closeEditNote,
            }}
        >
            {children}
        </NotesContext.Provider>
    )
}
